import React, { useMemo, useRef } from "react"

import { Input } from "antd"
import awesomeDebouncePromise from "awesome-debounce-promise"
import {
    type Control,
    Controller,
    type DeepMap,
    type FieldError,
    type FieldValues,
} from "react-hook-form"

import type { IIndexable } from "@common-shared/types/general.types"

import { useScrollErroredInputIntoView } from "../hooks/scrollErroredInputtIntoView.hook"
import { FloatingLabelElement } from "./floatingLabel.element"
import { InputErrorElement } from "./inputError.element"

export const TextAreaElement: React.FunctionComponent<
    ITextAreaElementProps
> = props => {
    const containerRef = useRef<HTMLDivElement>(null)

    useScrollErroredInputIntoView(
        props.name,
        containerRef,
        props.reactHookErrors,
        props.ignoreScrollIntoViewOnError,
    )

    const elementFullName = useMemo<string>(() => {
        return `${props.fieldGroupName ? `${props.fieldGroupName}.` : ""}${
            props.name
        }`
    }, [props.name, props.fieldGroupName])

    const inputHasError = useMemo<boolean>(() => {
        return !!props.reactHookErrors?.[props.name]
    }, [props.reactHookErrors?.[props.name]])

    const debouncedOnChange = useMemo(() => {
        if (!props.onChange) {
            return null
        }

        return awesomeDebouncePromise(props.onChange, props.debounce || 0)
    }, [props.onChange])

    const onTextAreaChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        originalOnChange?: (
            event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
        ) => void,
    ) => {
        if (debouncedOnChange) {
            event.persist() // So it can be used in debounce

            debouncedOnChange(event)
        }

        originalOnChange?.(event)
    }

    return (
        <div
            ref={containerRef}
            className={`
                relative
                ${props.className}
            `}
        >
            {props.label && (
                <FloatingLabelElement
                    label={props.label}
                    isFloated={true}
                    withRequiredStar={
                        props.withRequiredStar &&
                        props.reactHookValidations?.required
                    }
                    tooltip={props.tooltip}
                />
            )}

            <div className="relative">
                {props.reactHookControl ? (
                    <>
                        <Controller
                            render={({
                                field: { onChange, onBlur, value, name, ref },
                            }) => (
                                <Input.TextArea
                                    className={inputHasError ? "errored" : ""}
                                    value={value}
                                    name={name}
                                    ref={ref}
                                    style={{ fontSize: "16px" }}
                                    rows={props.rowsCount || 7}
                                    placeholder={props.placeholder}
                                    onChange={e => {
                                        onTextAreaChange(e, onChange)
                                    }}
                                    disabled={props.disabled}
                                    allowClear={props.allowClear}
                                    onBlur={onBlur}
                                />
                            )}
                            defaultValue={props.defaultValue}
                            name={elementFullName}
                            control={props.reactHookControl}
                            rules={props.reactHookValidations}
                            shouldUnregister={true}
                        />

                        {!props.hideErrors &&
                            props.reactHookErrors?.[props.name] && (
                                <InputErrorElement
                                    text={
                                        (
                                            props.reactHookErrors[
                                                props.name
                                            ] as FieldError
                                        ).message || "Error"
                                    }
                                />
                            )}
                    </>
                ) : (
                    <Input.TextArea
                        className={inputHasError ? "errored" : ""}
                        style={{ fontSize: "16px" }}
                        rows={props.rowsCount || 7}
                        placeholder={props.placeholder}
                        onChange={onTextAreaChange}
                        disabled={props.disabled}
                        defaultValue={props.defaultValue}
                        allowClear={props.allowClear}
                    />
                )}
            </div>
        </div>
    )
}

interface ITextAreaElementProps {
    className?: string
    name: string
    fieldGroupName?: string
    label?: string
    withRequiredStar?: boolean
    tooltip?: string
    allowClear?: boolean
    debounce?: number // in milliseconds
    reactHookControl?: Control
    reactHookValidations?: IIndexable
    reactHookErrors?: DeepMap<FieldValues, FieldError>
    defaultValue?: any
    placeholder?: string
    disabled?: boolean
    rowsCount?: number
    inputPrefix?: React.ReactNode
    onChange?: (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => void
    hideErrors?: boolean
    ignoreScrollIntoViewOnError?: boolean
}
